import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';
import * as XLSX from 'xlsx';

const AdminKonselingPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editedRequest, setEditedRequest] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });
  const [konselingRequests, setKonselingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  
  // Date formatting helper function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Check admin access
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const userRole = localStorage.getItem('userRole');
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    
    if (userData && isAuthenticated === 'true') {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Check if user is admin - check both parsedUser.role and userRole from localStorage
      if (parsedUser.role !== 'admin_user' && userRole !== 'admin_user') {
        navigate('/');
        return;
      }
    } else {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Fetch konseling requests from API
  const fetchKonselingRequests = async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/admin/konseling');
      if (response.ok) {
        const data = await response.json();
        setKonselingRequests(data.data || data);
      } else {
        console.error('Failed to fetch konseling requests');
        setNotification({
          show: true,
          message: 'Gagal memuat data permintaan konseling',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error fetching konseling requests:', error);
      setNotification({
        show: true,
        message: 'Terjadi kesalahan saat memuat data',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch admin users for decision maker info
  const fetchAdminUsers = async () => {
    try {
      const response = await authFetch('/api/admin_users');
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data.data || data);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
      setAdminUsers([]);
    }
  };

  // Helper function to get admin name by decision_by_id
  const getDecisionMakerName = (decisionById) => {
    if (!decisionById || !adminUsers.length) return null;
    const admin = adminUsers.find(admin => admin.id === decisionById);
    return admin ? admin.name : null;
  };

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    
    if (user?.role === 'admin_user' || userRole === 'admin_user') {
      fetchKonselingRequests();
      fetchAdminUsers();
    }
  }, [user]);

  const filteredRequests = konselingRequests.filter(request => {
    const teacherName = request.teacher_name || request.teacherName || '';
    const studentNames = Array.isArray(request.students) 
      ? request.students.map(s => s.name || s.student_name || '').join(' ')
      : request.student_name || '';
    const type = request.type || '';
    const description = request.description || '';
    
    return teacherName.toLowerCase().includes(searchQuery.toLowerCase()) ||
           studentNames.toLowerCase().includes(searchQuery.toLowerCase()) ||
           type.toLowerCase().includes(searchQuery.toLowerCase()) ||
           description.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Pagination logic
  const totalItems = filteredRequests.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredRequests.slice(startIndex, endIndex);

  // Reset current page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleApproval = async (requestId, action) => {
    try {
      
      
      // First, save all the form changes
      if (editedRequest) {
        
        const updateResponse = await authFetch(`/api/admin/konseling/${requestId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            date: editedRequest.date,
            teacher_name: editedRequest.teacher_name || editedRequest.teacherName,
            students: editedRequest.students || editedRequest.studentInfo || [],
            type: editedRequest.type,
            description: editedRequest.description,
            preferred_time: editedRequest.preferred_time || editedRequest.preferredTime,
            preferred_day: editedRequest.preferred_day || editedRequest.preferredDay,
            parent_knowledge: editedRequest.parent_knowledge || editedRequest.parentKnowledge,
          }),
        });

        if (!updateResponse.ok) {
          throw new Error('Failed to save form changes');
        }
      }
      
      // Then, update the status
      
      const statusResponse = await authFetch(`/api/admin/konseling/${requestId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: action === 'approve' ? 'approved' : 'rejected'
        }),
      });

      if (statusResponse.ok) {
        const result = await statusResponse.json();
       
        
        setNotification({
          show: true,
          message: `Permintaan konseling berhasil ${action === 'approve' ? 'disetujui' : 'ditolak'}`,
          type: 'success'
        });
        fetchKonselingRequests(); // Refresh the data
        setSelectedRequest(null);
        setEditedRequest(null);
      } else {
        let errorData;
        try {
          errorData = await statusResponse.json();
        } catch (e) {
          errorData = await statusResponse.text();
        }
        console.error('Error response:', errorData);
        throw new Error(`Failed to update status: ${statusResponse.status} ${statusResponse.statusText}`);
      }
    } catch (error) {
      console.error('Error updating konseling request:', error);
      setNotification({
        show: true,
        message: `Gagal mengupdate permintaan: ${error.message}`,
        type: 'error'
      });
    }
  };

  // When a request is selected, initialize editedRequest
  React.useEffect(() => {
    if (selectedRequest) {
      // Format dates properly for form inputs
      const formattedRequest = { ...selectedRequest };
      
      // Format date field for HTML date input (YYYY-MM-DD)
      if (selectedRequest.date) {
        formattedRequest.date = new Date(selectedRequest.date).toISOString().split('T')[0];
      } else if (selectedRequest.created_at) {
        formattedRequest.date = new Date(selectedRequest.created_at).toISOString().split('T')[0];
      }
      
      // Normalize teacher name field
      if (selectedRequest.teacherName && !selectedRequest.teacher_name) {
        formattedRequest.teacher_name = selectedRequest.teacherName;
      }
      
      // Format preferred_day to DD/MM/YYYY format
      if (selectedRequest.preferred_day && selectedRequest.preferred_day.includes('T')) {
        try {
          const dayDate = new Date(selectedRequest.preferred_day);
          const day = dayDate.getDate().toString().padStart(2, '0');
          const month = (dayDate.getMonth() + 1).toString().padStart(2, '0');
          const year = dayDate.getFullYear();
          formattedRequest.preferred_day = `${day}/${month}/${year}`;
        } catch (error) {
          // If parsing fails, keep original value
          formattedRequest.preferred_day = selectedRequest.preferred_day;
        }
      }
      
      setEditedRequest(formattedRequest);
    } else {
      setEditedRequest(null);
    }
  }, [selectedRequest]);

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Excel export function
  const handleExcelExport = async () => {
    try {
      setNotification({
        show: true,
        message: 'Memproses ekspor Excel...',
        type: 'success'
      });

      // Check if user is authenticated before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Build query parameters based on current filters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);

      console.log('Making Excel export request to:', `/api/admin/konseling/export-excel?${params.toString()}`);
      console.log('Authentication token present:', !!token);

      const response = await authFetch(`/api/admin/konseling/export-excel?${params.toString()}`, {
        method: 'PUT',
        autoLogoutOn401: false
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Get more detailed error information
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.text();
          console.log('Error response body:', errorData);
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          console.log('Could not read error response body');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Export data received:', data);
      
      // Validate data structure
      if (!data || !data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid data format received from server');
      }

      if (data.data.length === 0) {
        setNotification({
          show: true,
          message: 'Tidak ada data untuk diekspor dengan filter yang dipilih',
          type: 'error'
        });
        return;
      }
      
      // Create Excel workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data.data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Permintaan Konseling');
      
      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Permintaan_Konseling_${currentDate}.xlsx`;
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      setNotification({
        show: true,
        message: `File Excel berhasil diunduh: ${filename} (${data.data.length} records)`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error exporting Excel:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Gagal mengekspor data ke Excel';
      if (error.message.includes('No authentication token')) {
        errorMessage = 'Sesi telah berakhir. Silakan login kembali.';
      } else if (error.message.includes('401')) {
        errorMessage = 'Tidak memiliki akses. Silakan login kembali.';
      } else if (error.message.includes('403')) {
        errorMessage = 'Tidak memiliki izin untuk mengekspor data.';
      } else if (error.message.includes('404')) {
        errorMessage = 'Endpoint ekspor tidak ditemukan. Hubungi administrator.';
      } else if (error.message.includes('500')) {
        errorMessage = 'Terjadi kesalahan server. Coba lagi nanti.';
      } else if (error.message.includes('timeout') || error.message.includes('network')) {
        errorMessage = 'Koneksi timeout. Periksa koneksi internet Anda.';
      }
      
      setNotification({
        show: true,
        message: `${errorMessage} (Detail: ${error.message})`,
        type: 'error'
      });
    }
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Permintaan Konseling BK</h1>
            <Subtitle>Kelola dan tinjau permintaan konseling dari guru</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <SearchContainer>
                <SearchIcon>🔍</SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Cari berdasarkan nama guru, siswa, atau tipe konseling..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </SearchContainer>
              <ExportButton onClick={handleExcelExport}>
                📊 Export Excel
              </ExportButton>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal</th>
                    <th>Guru</th>
                    <th>Siswa</th>
                    <th>Tipe</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                        Memuat data...
                      </td>
                    </TableRow>
                  ) : filteredRequests.length === 0 ? (
                    <TableRow>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '2rem' }}>
                        Tidak ada data permintaan konseling
                      </td>
                    </TableRow>
                  ) : (
                    currentPageItems.map((request) => (
                      <TableRow 
                        key={request.id}
                        onClick={() => setSelectedRequest(request)}
                        selected={selectedRequest?.id === request.id}
                      >
                        <td>{formatDate(request.date || request.created_at)}</td>
                        <td>{request.teacher_name || request.teacherName || '-'}</td>
                        <td>
                          {Array.isArray(request.students) 
                            ? request.students.map(student => student.name || student.student_name).join(', ')
                            : request.student_name || request.studentInfo?.map(s => s.name).join(', ') || '-'
                          }
                        </td>
                        <td>{request.type || '-'}</td>
                        <td>
                          <StatusBadge status={request.status || request.request_status}>
                            {(request.status || request.request_status) === 'pending' ? 'Menunggu' : 
                             (request.status || request.request_status) === 'approved' ? 'Disetujui' : 
                             (request.status || request.request_status) === 'rejected' ? 'Ditolak' : 'Menunggu'}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedRequest(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, totalItems)} dari {totalItems} data
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <PageNumber
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          active={currentPage === pageNum}
                        >
                          {pageNum}
                        </PageNumber>
                      );
                    })}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedRequest && (
        <DetailModal onClick={() => setSelectedRequest(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <DetailHeader>
              <DetailHeaderContent>
                <h2>Edit Permintaan Konseling</h2>
                <StatusContainer>
                  <DetailStatus status={selectedRequest.status || selectedRequest.request_status}>
                    {(selectedRequest.status || selectedRequest.request_status) === 'pending' ? 'Menunggu' :
                      (selectedRequest.status || selectedRequest.request_status) === 'approved' ? 'Disetujui' : 
                      (selectedRequest.status || selectedRequest.request_status) === 'rejected' ? 'Ditolak' : 'Menunggu'}
                  </DetailStatus>
                  {(selectedRequest.status || selectedRequest.request_status) !== 'pending' && (
                    <DecisionMaker>
                      Diputuskan oleh: {getDecisionMakerName(selectedRequest.decision_by_id) || 'Herman Yosef'}
                    </DecisionMaker>
                  )}
                </StatusContainer>
              </DetailHeaderContent>
              <CloseButton onClick={() => setSelectedRequest(null)}>×</CloseButton>
            </DetailHeader>

            <div>
              <DetailContent>
                <DetailSection>
                  <SectionTitle>Informasi Umum</SectionTitle>
                  <DetailGrid>
                    <DetailGroup>
                      <DetailLabel>Tanggal Pengajuan</DetailLabel>
                      <FormInput
                        type="date"
                        value={editedRequest?.date || ''}
                        onChange={e => setEditedRequest({ ...editedRequest, date: e.target.value })}
                      />
                    </DetailGroup>
                    <DetailGroup>
                      <DetailLabel>Guru BK</DetailLabel>
                      <FormInput
                        type="text"
                        value={editedRequest?.teacher_name || ''}
                        onChange={e => setEditedRequest({ ...editedRequest, teacher_name: e.target.value })}
                      />
                    </DetailGroup>
                    <DetailGroup>
                      <DetailLabel>Tipe Pendampingan</DetailLabel>
                      <FormSelect
                        value={editedRequest?.type || selectedRequest?.type || 'Akademik'}
                        onChange={e => setEditedRequest({ ...editedRequest, type: e.target.value })}
                      >
                        <option value="Akademik">Akademik</option>
                        <option value="Pribadi">Pribadi</option>
                        <option value="Sosial">Sosial</option>
                        <option value="Karir">Karir</option>
                      </FormSelect>
                    </DetailGroup>
                  </DetailGrid>
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Siswa yang Diajukan</SectionTitle>
                  <StudentList>
                    {(editedRequest?.students || editedRequest?.studentInfo || selectedRequest?.students || selectedRequest?.studentInfo || []).map((student, index) => (
                      <StudentItem key={index}>
                        <StudentFieldsGrid>
                          <DetailGroup>
                            <DetailLabel>Nama Siswa</DetailLabel>
                            <FormInput
                              type="text"
                              value={student.name || student.student_name || ''}
                              onChange={e => {
                                const newStudents = [...(editedRequest?.students || editedRequest?.studentInfo || selectedRequest?.students || selectedRequest?.studentInfo || [])];
                                newStudents[index] = { ...newStudents[index], name: e.target.value };
                                setEditedRequest({ ...editedRequest, students: newStudents });
                              }}
                            />
                          </DetailGroup>
                          <DetailGroup>
                            <DetailLabel>Kelas</DetailLabel>
                            <FormInput
                              type="text"
                              value={student.kelas || student.class || student.student_class || ''}
                              onChange={e => {
                                const newStudents = [...(editedRequest?.students || editedRequest?.studentInfo || selectedRequest?.students || selectedRequest?.studentInfo || [])];
                                newStudents[index] = { ...newStudents[index], kelas: e.target.value };
                                setEditedRequest({ ...editedRequest, students: newStudents });
                              }}
                            />
                          </DetailGroup>
                          <DetailGroup>
                            <DetailLabel>NIS</DetailLabel>
                            <FormInput
                              type="text"
                              value={student.nis || student.student_id || ''}
                              onChange={e => {
                                const newStudents = [...(editedRequest?.students || editedRequest?.studentInfo || selectedRequest?.students || selectedRequest?.studentInfo || [])];
                                newStudents[index] = { ...newStudents[index], nis: e.target.value };
                                setEditedRequest({ ...editedRequest, students: newStudents });
                              }}
                            />
                          </DetailGroup>
                        </StudentFieldsGrid>
                      </StudentItem>
                    ))}
                  </StudentList>
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Detail Konseling</SectionTitle>
                  <DetailGrid>
                    <DetailGroup>
                      <DetailLabel>Waktu</DetailLabel>
                      <FormInput
                        type="text"
                        value={editedRequest?.preferred_time || ''}
                        onChange={e => setEditedRequest({ ...editedRequest, preferred_time: e.target.value })}
                      />
                    </DetailGroup>
                    <DetailGroup>
                      <DetailLabel>Tanggal</DetailLabel>
                      <FormInput
                        type="text"
                        value={editedRequest?.preferred_day || ''}
                        onChange={e => setEditedRequest({ ...editedRequest, preferred_day: e.target.value })}
                      />
                    </DetailGroup>
                  </DetailGrid>
                </DetailSection>

                <DetailSection>
                  <SectionTitle>Catatan</SectionTitle>
                  <DetailGroup full>
                    <FormTextarea
                      value={editedRequest?.description || selectedRequest?.description || ''}
                      onChange={e => setEditedRequest({ ...editedRequest, description: e.target.value })}
                      style={{ minHeight: '80px', resize: 'vertical' }}
                    />
                  </DetailGroup>
                </DetailSection>
              </DetailContent>

              <ActionButtons>
                <RejectButton type="button" onClick={() => handleApproval(selectedRequest?.id, 'reject')}>
                  Tolak
                </RejectButton>
                <ApproveButton type="button" onClick={() => handleApproval(selectedRequest?.id, 'approve')}>
                  Setujui
                </ApproveButton>
              </ActionButtons>
            </div>
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast $type={notification.type}>
          <NotificationMessage>{notification.message}</NotificationMessage>
          <CloseNotificationButton onClick={() => setNotification({ ...notification, show: false })}>
            ×
          </CloseNotificationButton>
        </NotificationToast>
      )}
    </PageContainer>
  );
}

// Reuse the same styled components from AdminLaporanKTSPage
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  flex: 1;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch(props.status) {
      case 'pending': return '#fff3e0';
      case 'approved': return '#e6f8f5';
      case 'rejected': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'pending': return '#e67700';
      case 'approved': return '#2ec27e';
      case 'rejected': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StudentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StudentItem = styled.div`
  background-color: var(--background-light);
  padding: 0.75rem;
  border-radius: 8px;
`;

const StudentFieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const DetailValue = styled.div`
  font-size: 0.938rem;
  color: var(--text-dark);
  line-height: 1.5;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  background-color: ${props => props.$type === 'success' ? '#e6f8f5' : '#ffe6e6'};
  color: ${props => props.$type === 'success' ? '#2ec27e' : '#c01c28'};
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  min-width: 300px;
`;

const NotificationMessage = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
`;

const CloseNotificationButton = styled.button`
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: inherit;
  margin-left: 1rem;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

const ExportButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  justify-content: center;

  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default AdminKonselingPage;